/** Milestone keys tracked by the progressive disclosure backend. */
export type MilestoneKey =
  | 'first_login'
  | 'customer_created'
  | 'quotation_submitted'
  | 'invoice_posted'
  | 'delivery_complete'
  | 'team_invited'
  | 'template_customized'
  | 'integration_connected'

export type UserTier = 'starter' | 'explorer' | 'practitioner' | 'operator' | 'power'

/**
 * Navigation gate ids evaluated server-side from the tenant's own documents.
 * Each gate turns on as soon as the tenant has produced *any one* of the
 * documents that feed it — so a tenant can reach quotations after contracts,
 * contracts after invoices, items before delivery notes, and so on.
 */
export type GateId =
  | 'quotations'
  | 'contracts'
  | 'sales_orders'
  | 'projects'
  | 'delivery_notes'
  | 'invoices'
  | 'bills'
  | 'payments'
  | 'accounting_reports'

export const GATE_IDS: GateId[] = [
  'quotations',
  'contracts',
  'sales_orders',
  'projects',
  'delivery_notes',
  'invoices',
  'bills',
  'payments',
  'accounting_reports',
]

/** Gate open/closed flags as reported by the backend capability catalog. */
export type NavigationGates = Record<GateId, boolean>

export function isGateId(value: string): value is GateId {
  return (GATE_IDS as string[]).includes(value)
}

export type MilestoneEntry = {
  key: MilestoneKey
  completed_at?: string | null
  ack_at?: string | null
}

export type MilestoneState = {
  tier: UserTier
  completed_keys: MilestoneKey[]
  unacked_keys: MilestoneKey[]
  milestones: MilestoneEntry[]
  next_key?: MilestoneKey | null
}

export const MILESTONE_ORDER: MilestoneKey[] = [
  'first_login',
  'customer_created',
  'quotation_submitted',
  'invoice_posted',
  'delivery_complete',
  'team_invited',
  'template_customized',
  'integration_connected',
]

export const MILESTONE_TIER: Record<MilestoneKey, UserTier> = {
  first_login: 'starter',
  customer_created: 'starter',
  quotation_submitted: 'explorer',
  invoice_posted: 'practitioner',
  delivery_complete: 'operator',
  team_invited: 'operator',
  template_customized: 'power',
  integration_connected: 'power',
}

/** Deep-link when a milestone unlock toast is dismissed or CTA clicked. */
export const MILESTONE_UNLOCK_ROUTES: Partial<Record<MilestoneKey, string>> = {
  customer_created: '/quotations/new',
  quotation_submitted: '/contracts/new',
  invoice_posted: '/invoices/new',
  delivery_complete: '/delivery-notes/new',
  team_invited: '/admin',
  template_customized: '/settings/template-studio',
  integration_connected: '/settings/daftra',
}

export type MilestoneCatalogItem = {
  key: MilestoneKey
  labelKey: string
  actionRoute?: string
}

export const MILESTONE_CATALOG: MilestoneCatalogItem[] = [
  { key: 'first_login', labelKey: 'milestones.items.first_login' },
  { key: 'customer_created', labelKey: 'milestones.items.customer_created', actionRoute: '/customers' },
  { key: 'quotation_submitted', labelKey: 'milestones.items.quotation_submitted', actionRoute: '/quotations/new' },
  { key: 'invoice_posted', labelKey: 'milestones.items.invoice_posted', actionRoute: '/invoices/new' },
  { key: 'delivery_complete', labelKey: 'milestones.items.delivery_complete', actionRoute: '/delivery-notes/new' },
  { key: 'team_invited', labelKey: 'milestones.items.team_invited', actionRoute: '/admin/access/users' },
  { key: 'template_customized', labelKey: 'milestones.items.template_customized', actionRoute: '/settings/template-studio' },
  { key: 'integration_connected', labelKey: 'milestones.items.integration_connected', actionRoute: '/settings/daftra' },
]

export function computeTierFromKeys(completedKeys: MilestoneKey[]): UserTier {
  let tier: UserTier = 'starter'
  for (const key of completedKeys) {
    const keyTier = MILESTONE_TIER[key]
    const tierRank = ['starter', 'explorer', 'practitioner', 'operator', 'power'] as const
    if (tierRank.indexOf(keyTier) > tierRank.indexOf(tier)) {
      tier = keyTier
    }
  }
  return tier
}

export function nextMilestoneKey(completedKeys: MilestoneKey[]): MilestoneKey | null {
  for (const key of MILESTONE_ORDER) {
    if (!completedKeys.includes(key)) return key
  }
  return null
}
